/**
 * Financial position:  Opening balance + Income − Expenses = Closing balance.
 *
 * The closing balance of a month is the opening balance of the next one. A balance anchor (e.g.
 * the workbook's "Saldo di Partenza" at 2026-01) sets the opening balance of its month; months
 * before the first anchor start from zero and are flagged `anchored: false`.
 */
import { compareMonths, monthRange } from "@/domain/accounting-period";
import type { YearMonth } from "@/domain/model";
import { Decimal, sum, toDecimalString, type DecimalString } from "@/domain/money";
import type { FinanceIndex } from "./data";

export interface BalanceRow {
  month: YearMonth;
  opening: DecimalString;
  income: DecimalString;
  expenses: DecimalString;
  /** income − expenses (the month's savings) */
  net: DecimalString;
  closing: DecimalString;
  /** True once a balance anchor applies to this month or an earlier one. */
  anchored: boolean;
  /** Label of the anchor that set this month's opening balance, if any. */
  anchorLabel: string | null;
}

export function monthIncomeTotal(index: FinanceIndex, month: YearMonth): Decimal {
  return sum((index.incomesByMonth.get(month) ?? []).map((i) => i.amount));
}

export function monthExpenseTotal(index: FinanceIndex, month: YearMonth): Decimal {
  return sum((index.expensesByMonth.get(month) ?? []).map((e) => e.amountEur));
}

/** Balance rows for every month in [from, to], carrying balances forward from earlier anchors/data. */
export function computeBalanceSeries(index: FinanceIndex, from: YearMonth, to: YearMonth): BalanceRow[] {
  if (compareMonths(from, to) > 0) return [];
  const earlier = [...index.anchors.keys(), ...index.dataMonths].filter((m) => compareMonths(m, from) < 0).sort();
  const start = earlier[0] ?? from;

  const rows: BalanceRow[] = [];
  let carried = new Decimal(0);
  let anchored = false;
  for (const month of monthRange(start, to)) {
    const anchor = index.anchors.get(month);
    let opening = carried;
    if (anchor) {
      opening = new Decimal(anchor.amount);
      anchored = true;
    }
    const income = monthIncomeTotal(index, month);
    const expenses = monthExpenseTotal(index, month);
    const closing = opening.plus(income).minus(expenses);
    if (compareMonths(month, from) >= 0) {
      rows.push({
        month,
        opening: toDecimalString(opening),
        income: toDecimalString(income),
        expenses: toDecimalString(expenses),
        net: toDecimalString(income.minus(expenses)),
        closing: toDecimalString(closing),
        anchored,
        anchorLabel: anchor?.label ?? null,
      });
    }
    carried = closing;
  }
  return rows;
}

export function balanceAt(index: FinanceIndex, month: YearMonth): BalanceRow {
  const [row] = computeBalanceSeries(index, month, month);
  return row!;
}
